import { useState } from 'react';

type Params<T> = {
  /** Ключ записи: по нему снимается отметка */
  getItemId: (item: T) => string | number;
  /**
   * Записи, отмеченные при открытии окна; без него окно открывается без отметок. Читается на каждой
   * отрисовке открытого окна: состав, сопоставленный со справочником, пришедшим уже после открытия,
   * появляется в окне отмеченным
   */
  getInitialSelected?: () => T[];
};

/**
 * Состояние окна выбора из списка: открыто ли окно, набранный запрос и отмеченные записи в порядке отметки.
 *
 * Окно держит не сами отметки, а правки пользователя поверх исходных: отмеченные сверх них записи
 * и снятые с них ключи. Поэтому исходные отметки, которые стали известны после открытия окна, встают
 * отмеченными, а уже сделанные отметки и снятия сохраняются.
 *
 * `group` — строки группы отмеченных: исходные отметки, в том числе снятые, и за ними отмеченные
 * сверх исходных. Снятая исходная запись остаётся в группе до закрытия окна: в выдаче её может
 * не быть, и вне группы она пропала бы из окна целиком, а отметку вернуть было бы негде.
 *
 * Каждое открытие начинается с исходных отметок, а закрытие отказывается от набранного запроса: сброс
 * запроса при открытии успел бы отправить прежний запрос в окно с поиском на сервере, пока не истекла
 * задержка набора. `isSelectionChanged` — отметки разошлись с исходными: окну, открытому с текущим
 * составом, подтверждать без этого нечего.
 */
export const useSelectionDialogState = <T>({ getItemId, getInitialSelected }: Params<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [checked, setChecked] = useState<T[]>([]);
  const [uncheckedIds, setUncheckedIds] = useState<ReadonlySet<string | number>>(() => new Set());

  const initial = isOpen ? (getInitialSelected?.() ?? []) : [];
  const initialIds = new Set(initial.map(getItemId));
  const checkedBeyondInitial = checked.filter((item) => !initialIds.has(getItemId(item)));
  const selected = [...initial.filter((item) => !uncheckedIds.has(getItemId(item))), ...checkedBeyondInitial];
  const group = [...initial, ...checkedBeyondInitial];

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setChecked([]);
      setUncheckedIds(new Set());
    } else setSearch('');
    setIsOpen(open);
  };

  const handleToggle = (item: T, isChecked: boolean) => {
    const id = getItemId(item);
    setChecked((prev) => {
      const rest = prev.filter((entry) => getItemId(entry) !== id);
      return isChecked ? [...rest, item] : rest;
    });
    setUncheckedIds((prev) => {
      const next = new Set(prev);
      if (isChecked) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isSelectionChanged =
    selected.length !== initialIds.size || selected.some((item) => !initialIds.has(getItemId(item)));

  return { isOpen, search, setSearch, selected, group, isSelectionChanged, handleOpenChange, handleToggle };
};
